import React from 'react';
import { Card, Group, Text, Button, Box, Image, useMantineTheme } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { useMediaQuery } from '@mantine/hooks';

type EmptyPantryCTAProps = {
  onAddPantryItems: () => void;
};

export const EmptyPantryCTA: React.FC<EmptyPantryCTAProps> = ({
  onAddPantryItems
}) => {
  const theme = useMantineTheme();
  const isDesktop = useMediaQuery(`(min-width: ${theme.breakpoints.md})`);
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);
  
  if (isMobile) {
    // Mobile version with vertical layout
    return (
      <Card 
        radius="lg"
        p="md"
        style={{ 
          background: 'linear-gradient(135deg, #f7f9fa 0%, #f1f5f9 100%)',
          border: '1px solid #cbd5e1',
          boxShadow: '0 1px 3px rgba(71, 85, 105, 0.08)'
        }}
      >
        <Group align="center" mb="sm">
          {/* Small pantry photo as accent */}
          <Box 
            style={{ 
              width: 48,
              height: 48,
              borderRadius: '50%',
              overflow: 'hidden',
              flexShrink: 0,
              border: '2px solid #cbd5e1',
              cursor: 'pointer'
            }}
            onClick={onAddPantryItems}
          >
            <Image
              src="/images/pantry.jpeg"
              alt="Pantry example"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />
          </Box>
          
          <Box style={{ flex: 1 }}>
            <Text fw={600} size="sm" c="dark" mb={2}>
              What's in your pantry?
            </Text>
            <Text size="xs" c="dimmed">
              Meal plans use what you have, shopping lists skip it
            </Text>
          </Box>
        </Group>
        
        <Button 
          size="sm"
          variant="light"
          color="blue"
          leftSection={<IconPlus size={16} />}
          onClick={onAddPantryItems}
          fullWidth
          style={{ 
            fontWeight: 600
          }}
        >
          Add pantry items
        </Button>
      </Card>
    );
  }
  
  return (
    <Card 
      radius="xl"
      p={0}
      style={{ 
        overflow: 'hidden',
        background: 'white',
        position: 'relative',
        border: '1px solid rgba(0, 0, 0, 0.06)',
        transition: 'all 0.2s ease'
      }}
      className="pantry-cta"
    >
      <Group gap={0} align="stretch">
        {/* Pantry Photo */}
        <Box
          style={{
            width: 200,
            // The content column's height varies with subtitle wrapping; a
            // fixed height left a band of the content background exposed
            // below the photo whenever the row ran taller. Stretch with the
            // row (Group align="stretch") and let the image cover.
            minHeight: 110,
            position: 'relative',
            overflow: 'hidden',
            flexShrink: 0,
            cursor: 'pointer'
          }}
          onClick={onAddPantryItems}
        >
          <Image
            src="/images/pantry.jpeg"
            alt="Well-stocked pantry"
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              filter: 'brightness(1.05) contrast(1.1) saturate(1.1)'
            }}
          />

          {/* Subtle overlay gradient */}
          <Box
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(90deg, transparent 50%, rgba(0,0,0,0.03) 80%, rgba(255,255,255,0.1) 100%)',
              pointerEvents: 'none'
            }}
          />
        </Box>

        {/* Content Section */}
        <Box style={{ 
          flex: 1, 
          padding: isDesktop ? 'var(--mantine-spacing-lg)' : 'var(--mantine-spacing-md)',
          background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.95) 100%)'
        }}>
          <Group justify="space-between" align="center" h="100%">
            <Box style={{ flex: 1 }}>
              <Text 
                fw={700} 
                size={isDesktop ? "md" : "sm"}
                c="dark" 
                mb={isDesktop ? 6 : 4}
                style={{ 
                  letterSpacing: '-0.01em',
                  lineHeight: 1.2
                }}
              >
                What's in your pantry?
              </Text>
              <Text 
                size={isDesktop ? "sm" : "xs"}
                c="dimmed" 
                style={{ 
                  lineHeight: isDesktop ? 1.4 : 1.3,
                  color: 'rgba(0,0,0,0.65)'
                }}
              >
                {isDesktop
                  ? "Tell us what you have at home. Meal plans can cook with it, and shopping lists skip what you already own"
                  : "Meal plans use what you have, shopping lists skip it"
                }
              </Text>
            </Box>
            
            <Button 
              size={isDesktop ? "md" : "sm"}
              variant="gradient"
              gradient={{ from: 'blue', to: 'cyan', deg: 45 }}
              leftSection={<IconPlus size={isDesktop ? 16 : 14} />}
              onClick={onAddPantryItems}
              style={{ 
                flexShrink: 0,
                marginLeft: isDesktop ? 'var(--mantine-spacing-lg)' : 'var(--mantine-spacing-md)',
                fontWeight: 600,
                letterSpacing: '0.01em',
                boxShadow: '0 2px 8px rgba(59, 130, 246, 0.25)',
                border: 'none',
                width: isDesktop ? '180px' : 'auto'
              }}
            >
              Add items
            </Button>
          </Group>
        </Box>
      </Group>
    </Card>
  );
};